import { useState, useEffect, useRef } from "react";
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import { 
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Comment as CommentIcon
} from "@mui/icons-material";
import axios from "axios";
import CommentItem from "./CommentItem";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Role mapping for display
const ROLE_DISPLAY_NAMES = {
  assignee: "Assignee",
  reviewer: "Reviewer",
  watcher: "Watcher",
  productOwner: "Product Owner",
  reporter: "Reporter",
  viewer: "Viewer",
  observer: "Observer",
  custom: "Add Custom Role"
};

// Role colors
const ROLE_COLORS = {
  assignee: "#0052CC",
  reviewer: "#FFAB00",
  watcher: "#6554C0",
  productOwner: "#36B37E",
  reporter: "#DE350B",
  viewer: "#6B778C",
  observer: "#FF8B00",
  custom: "#6B778C"
};

const CommentsSection = ({ 
  issueId, 
  currentEmployeeId,
  issueDetails, // Pass the full issue object
  getCompanyId 
}) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [customRole, setCustomRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const commentsEndRef = useRef(null);

  // Get user's assigned roles for this issue
  const getUserAssignedRoles = () => {
    if (!currentEmployeeId || !issueDetails) return [];
    
    const roles = [];
    const empId = String(currentEmployeeId);
    
    // Check each possible role
    if (issueDetails.assignee && String(issueDetails.assignee) === empId) {
      roles.push("assignee");
    }
    if (issueDetails.reviewer && String(issueDetails.reviewer) === empId) {
      roles.push("reviewer");
    }
    if (issueDetails.watcher && String(issueDetails.watcher) === empId) {
      roles.push("watcher");
    }
    if (issueDetails.productOwner && String(issueDetails.productOwner) === empId) {
      roles.push("productOwner");
    }
    if (issueDetails.reporter && String(issueDetails.reporter) === empId) {
      roles.push("reporter");
    }
    
    return roles;
  };

  // Get dropdown role options for this user
  const getRoleOptions = () => {
    const assignedRoles = getUserAssignedRoles();
    
    // Start with assigned roles (if any)
    const options = assignedRoles.map(role => ({
      value: role,
      label: ROLE_DISPLAY_NAMES[role] || role,
      color: ROLE_COLORS[role] || "#6B778C"
    }));
    
    // Always add these options
    const alwaysOptions = [
      { value: "viewer", label: "Viewer", color: ROLE_COLORS.viewer },
      { value: "observer", label: "Observer", color: ROLE_COLORS.observer },
      { value: "custom", label: "Add Custom Role", color: ROLE_COLORS.custom }
    ];
    
    return [...options, ...alwaysOptions];
  };

  // Get default selected role (first assigned role, if any)
  const getDefaultRole = () => {
    const assignedRoles = getUserAssignedRoles();
    return assignedRoles.length > 0 ? assignedRoles[0] : "";
  };

  // Initialize selected role
  useEffect(() => {
    if (issueDetails && currentEmployeeId) {
      const defaultRole = getDefaultRole();
      setSelectedRole(defaultRole);
    }
  }, [issueDetails, currentEmployeeId]);

  // Get final role (either selected or custom)
  const getFinalRole = () => {
    if (selectedRole === "custom") {
      return customRole.trim();
    }
    return selectedRole;
  };

  // Get role label for display
  const getRoleLabel = (roleValue) => {
    if (roleValue === "custom") return customRole.trim();
    return ROLE_DISPLAY_NAMES[roleValue] || roleValue;
  };

  // Get role color
  const getRoleColor = (roleValue) => {
    return ROLE_COLORS[roleValue] || "#6B778C";
  };

  // Fetch comments
  const fetchComments = async () => {
    if (!issueId) return;
    
    try {
      const company_id = getCompanyId();
      const token = localStorage.getItem('token');
      setLoading(true);
      setError(null);
      
      const response = await axios.get(
        `${API_URL}/api/issue-comment/${issueId}/comments`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          
          }
        }
      );
      
      if (response.data && response.data.success) {
        setComments(response.data.data || []);
      } else {
        setComments([]);
      }
    } catch (err) {
      setError("Failed to load comments: " + err.message);
      setComments([]);
    } finally {
      setLoading(false);
    }
  };

  // Add new comment
  const handleAddComment = async () => {
    const finalRole = getFinalRole();
    
    if (!newComment.trim() || !currentEmployeeId || !finalRole) {
      setError("Please enter comment and select a role");
      return;
    }
    
    try {
      const company_id = getCompanyId();
      const token = localStorage.getItem('token');
      setPosting(true);
      setError(null);
      
      const response = await axios.post(
        `${API_URL}/api/issue-comment/${issueId}/comments`,
        {
          comment_text: newComment.trim(),
          employee_id: currentEmployeeId,
          role: finalRole,
        },{
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      if (response.data && response.data.success) {
        // Add the new comment to the list
        setComments([response.data.data, ...comments]);
        
        // Reset form but keep selected role
        setNewComment("");
        setCustomRole("");
        
        // Scroll to bottom
        setTimeout(() => {
          commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    } catch (err) {
      setError("Failed to add comment: " + err.message);
    } finally {
      setPosting(false);
    }
  };

  // Edit comment
  const handleEditComment = async (commentId, newText) => {
    try {
      const company_id = getCompanyId();
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/issue-comment/${issueId}/comments/${commentId}`,
        {
          comment_text: newText.trim(),
          employee_id: currentEmployeeId,
        },{
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      if (response.data && response.data.success) {
        setComments(comments.map(comment => 
          comment.id === commentId ? response.data.data : comment
        ));
      }
    } catch (err) {
      setError("Failed to edit comment: " + err.message);
    }
  };

  // Delete comment
  const handleDeleteComment = async (commentId) => {
    try {
      const company_id = getCompanyId();
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API_URL}/api/issue-comment/${issueId}/comments/${commentId}`,
        {
          data: { employee_id: currentEmployeeId },
     
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      if (response.data && response.data.success) {
        setComments(comments.filter(comment => comment.id !== commentId));
      }
    } catch (err) {
      setError("Failed to delete comment: " + err.message);
    }
  };

  // Handle Enter key to submit
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleAddComment();
    }
  };

  useEffect(() => {
    if (issueId && expanded) {
      fetchComments();
    }
  }, [issueId, expanded]);

  const roleOptions = getRoleOptions();
  const showCustomRoleInput = selectedRole === "custom";
  const hasAssignedRole = getUserAssignedRoles().length > 0;

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header */}
      <Box 
        sx={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "space-between",
          mb: 2,
          cursor: "pointer",
          "&:hover": { opacity: 0.8 }
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CommentIcon fontSize="small" sx={{ color: "#0052CC" }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
            Comments ({comments.length})
          </Typography>
        </Box>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box>
          {/* Error message */}
          {error && (
            <Alert 
              severity="warning" 
              sx={{ mb: 2, fontSize: "0.8rem" }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* Comment input */}
          <Box sx={{ mb: 3, p: 1.5, bgcolor: "#FAFBFC", borderRadius: 1, border: "1px solid #DFE1E6" }}>
            {/* Role selection */}
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <InputLabel id="role-select-label">Your Role *</InputLabel>
              <Select
                labelId="role-select-label"
                value={selectedRole}
                label="Your Role *"
                onChange={(e) => setSelectedRole(e.target.value)}
                disabled={posting}
              >
                <MenuItem value="">
                  <em>Select your role</em>
                </MenuItem>
                {roleOptions.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: role.color,
                        }}
                      />
                      {role.label}
                      {hasAssignedRole && getUserAssignedRoles().includes(role.value) && (
                        <Typography variant="caption" sx={{ color: "#6B778C", ml: 1 }}>
                          (Auto-detected)
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Custom role input (shown when "Add Custom Role" is selected) */}
            {showCustomRoleInput && (
              <TextField
                fullWidth
                size="small"
                placeholder="Enter your custom role (e.g., Security Auditor, DevOps Engineer)"
                value={customRole}
                onChange={(e) => setCustomRole(e.target.value)}
                disabled={posting}
                sx={{ mb: 1.5 }}
              />
            )}

            {/* Comment text */}
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Type your comment here..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={posting || !selectedRole}
              variant="outlined"
              size="small"
              sx={{ mb: 1.5 }}
            />

            {/* Action buttons */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setNewComment("");
                  setCustomRole("");
                }}
                disabled={!newComment.trim() || posting}
              >
                Clear
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleAddComment}
                disabled={!newComment.trim() || !selectedRole || posting}
                startIcon={posting ? <CircularProgress size={16} /> : <SendIcon />}
              >
                {posting ? "Posting..." : "Post Comment"}
              </Button>
            </Box>

            {/* Helper text */}
            {selectedRole && (
              <Typography variant="caption" sx={{ color: "#6B778C", display: "block", mt: 1 }}>
                Commenting as: <strong>{getRoleLabel(selectedRole)}</strong>
                {hasAssignedRole && getUserAssignedRoles().includes(selectedRole) && " (your assigned role)"}
              </Typography>
            )}
          </Box>

          {/* Comments list */}
          <Box sx={{ maxHeight: 400, overflow: "auto", pr: 1 }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : comments.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4, color: "#6B778C" }}>
                <CommentIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
                <Typography variant="body2">
                  No comments yet. Be the first to comment!
                </Typography>
              </Box>
            ) : (
              <>
                {comments.map((comment) => (
                  <CommentItem
                    key={comment.id}
                    comment={comment}
                    currentEmployeeId={currentEmployeeId}
                    onEdit={handleEditComment}
                    onDelete={handleDeleteComment}
                  />
                ))}
                <div ref={commentsEndRef} />
              </>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default CommentsSection;